import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import moment from "moment-timezone";
import Dialog from "components/shared/Dialog";
import { Button } from "components/ui/button";
import Form, { Field } from "components/shared/Form";
import DateInput from "components/shared/inputs/DateInput";
import { Textarea } from "components/ui/textarea";
import Avatar from "components/shared/Avatar";
import { renderUserName } from "utils/helpers/user";
import { useCompanyDateFormat, useCompanyTimeFormat } from "utils/hooks/company";
import type { TFunction } from "i18next";
import type { SegmentActionTarget } from "./types";

interface CloseDialogProps {
    target: SegmentActionTarget | null;
    close: () => void;
    onSubmit?: (target: SegmentActionTarget, to: string, reason: string) => void;
}

type CloseFormValues = {
    to?: Date;
    reason: string;
};

const createSchema = (t: TFunction) => z.object({
    to: z.any().refine((value) => !!value, {
        message: t("required-end-date"),
    }),
    reason: z.string().optional(),
});

function CloseDialog({ target, close, onSubmit: onCloseSubmit }: CloseDialogProps) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();

    const schema = useMemo(() => createSchema(t), [t]);
    const form = useForm<CloseFormValues>({
        defaultValues: { to: undefined, reason: "" },
        resolver: zodResolver(schema),
    });

    const minDate = target && target.segment.periodFrom
        ? moment.max(moment().startOf("day"), moment(target.segment.periodFrom).startOf("day")).add(1, "day")
        : moment().add(1, "day").startOf("day");

    useEffect(() => {
        if (target) {
            form.reset({ to: minDate.toDate(), reason: "" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target]);

    const timeLabel = target
        ? (target.segment.allDay
            ? t("all-day")
            : `${moment.parseZone(target.segment.from).format(timeFormat)} – ${moment.parseZone(target.segment.to).format(timeFormat)}`)
        : "";

    const title = target ? (
        <div className="tw-flex tw-items-center tw-gap-2">
            <Avatar user={target.user} size="sm" />
            <span className="tw-text-sm tw-font-semibold">{renderUserName(target.user)}</span>
        </div>
    ) : null;

    const onFinish = (values: CloseFormValues) => {
        if (!target || !values.to) return;
        const formattedTo = moment(values.to).format("YYYY-MM-DD");
        onCloseSubmit?.(target, formattedTo, values.reason ?? "");
        toast.success(`${t("request-closed")}: ${renderUserName(target.user)} — ${moment(values.to).format(dateFormat)}`);
        close();
    };

    return (
        <Dialog
            title={title}
            open={!!target}
            close={(isOpen) => !isOpen && close()}
            size="lg"
            footer={
                <>
                    <Button type="button" variant="outline" onClick={close}>
                        {t("cancel")}
                    </Button>
                    <Button type="submit" form="close-request-form">
                        {t("close")}
                    </Button>
                </>
            }
        >
            {target && (
                <Form id="close-request-form" form={form} onFinish={onFinish}>
                    <div className="tw-flex tw-flex-col tw-gap-0.5 tw-rounded-lg tw-border tw-border-border/60 tw-bg-muted/30 tw-px-4 tw-py-3 tw-mb-6">
                        <span className="tw-text-sm tw-font-medium">{target.label}</span>
                        <span className="tw-text-xs tw-text-muted-foreground">{timeLabel}</span>
                    </div>

                    <Field name="to" label={t("end-date")} required>
                        <DateInput mode="single" placeholder={t("select-date-range")} minDate={minDate.toDate()} />
                    </Field>

                    <Field name="reason" label={t("reason")}>
                        <Textarea placeholder={t("reason")} />
                    </Field>
                </Form>
            )}
        </Dialog>
    );
}

export default CloseDialog;
